import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
//import AllBatches from './components/AllBatches';
import BatchDetails from './components/BatchDetails';
import AddBatches from "./components/AddBatches";
import UpdateBatches from "./components/UpdateBatches";
import NotesTable from "./components/NotesTable";
import AllStudent from "./components/AllStudent";
import AddStudent from "./components/AddStudent";
import StudentDetails from "./components/StudentDetails";
import UpdateStudent from "./components/UpdateStudent";
import NotificationManagement from "./components/NotificationManagement";
import PageNotFound from "./components/PageNotFound";
import "./styles/Sidebar.css";
import AddAssignments from "./components/AddAssignment";
import AllAssignments from "./components/AllAssignments";
import AssignmentDetails from "./components/AssignmentDetails";
import UpdateAssignment from "./components/UpdateAssignments";
import AddQuestions from "./components/AddQuestions";
import AllQuestions from "./components/AllQuestions";
import QuestionDetails from "./components/QuestionDetails";
import UpdateQuestion from "./components/UpdateQuestions";
import TestList from "./components/TestList";
import Navigation from "./components/Navigation";
import TestDetails from "./components/TestDetails";
import AddTests from "./components/AddTests";
import UpdateTest from "./components/UpdateTests";
import BlankLayout from "./components/BlankLayout";
import Login from "./components/Login";
import OtpVerification from "./components/OtpVerification";
import Dashboard from "./components/Dashboard";
import PerformanceTracking from "./components/PerformanceTracking";
import TestResultsDetail from "./components/TestResultsDetail";
import StudentResponses from "./components/StudentResponses";
import ToastMessage from "./components/ReusableComponent/ToastMessage";
import { jwtDecode } from "jwt-decode";
import ZoomMeetings from "./components/ZoomMeetings";
import BatchList from './components/BatchList';
import CourseList from './components/CourseList';
import CourseDetails from './components/CourseDetails';
import AddCourse from './components/AddCourse';
import UserManagement from './components/UserManagement';
import UserPermissions from './components/UserPermissions';
import UpdateCourse from './components/UpdateCourse';
import NotesManagementPage from './components/notes/NotesManagementPage';
import AddSubjectPage from './components/notes/AddSubjectPage';
import SubjectDetailsPage from './components/notes/SubjectDetailsPage';
import TopicPage from './components/notes/TopicPage';
import SectionContentPage from './components/notes/SectionContentPage';
import NoteDetailsPage from './components/notes/NoteDetailsPage';
import CourseNotesPage from './components/notes/CourseNotesPage';
import TopicContentPage from './components/notes/TopicContentPage';
import AddEditNote from './components/notes/AddEditNote';
import TrainerList from './components/TrainerList';
import AddTrainer from './components/AddTrainer';
import UpdateTrainer from './components/UpdateTrainer';
import TrainerDetails from './components/TrainerDetails';
import LeadDashboard from './components/leads/LeadDashboard';
import LeadList from './components/leads/LeadList';
import LeadDetails from './components/leads/LeadDetails';
import LeadSetup from './components/leads/LeadSetup';
import LeadNotificationLog from './components/leads/LeadNotificationLog';
import LeadRegistrations from './components/leads/LeadRegistrations';
import SystemHealth from './components/health/SystemHealth';

function App() {
  const [token, setToken] = useState(localStorage.getItem("accessToken"));
  const [showLoginToast, setShowLoginToast] = useState(false);

  // Toast auto-hide logic
  useEffect(() => {
    if (showLoginToast) {
      const timer = setTimeout(() => {
        setShowLoginToast(false);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [showLoginToast]);

  // Token validation logic
  useEffect(() => {
    const checkToken = () => {
      const currentToken = localStorage.getItem("accessToken");

      if (!currentToken) {
        setToken(null);
        return;
      }

      try {
        const decoded = jwtDecode(currentToken);
        const currentTime = Date.now() / 1000;

        if (decoded.exp < currentTime) {
          console.log("Token expired!");
          localStorage.removeItem("accessToken");
          setToken(null);
          setShowLoginToast(true);
        } else {
          setToken(currentToken);
        }
      } catch (error) {
        console.error("Invalid token");
        localStorage.removeItem("accessToken");
        setToken(null);
      }
    };
    checkToken();

    const interval = setInterval(checkToken, 5000);

    const handleStorageChange = () => {
      if (!localStorage.getItem("accessToken")) {
        setToken(null);
        setShowLoginToast(true);
      }
    };

    // Listen for localStorage changes
    window.addEventListener("storage", handleStorageChange);

    // Cleanup interval and event listener
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  return (
    <BrowserRouter>
      {!token && showLoginToast && (
        <ToastMessage message="Your session has expired. Please login again!" />
      )}
      <Routes>
        <Route element={<BlankLayout />}>
          <Route
            path="/"
            element={token ? <Navigate to="/dashboard" replace /> : <Login />}
          />
          <Route
            path="/login"
            element={token ? <Navigate to="/dashboard" replace /> : <Login />}
          />
          <Route path="/otp" element={<OtpVerification />} />
        </Route>

        <Route
          element={token ? <Navigation /> : <Navigate to="/login" replace />}
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/performance-tracking" element={<PerformanceTracking />} />
          <Route path="/test-results" element={<TestResultsDetail />} />
          <Route path="/student-responses" element={<StudentResponses />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="users/:userType/:id" element={<UserPermissions />} />
          <Route path="courses" element={<CourseList />} />
          {/* Before courses/:id so the intent is obvious reading top-down — React
              Router ranks the static segment higher anyway, but "add" must never
              be mistaken for a course id. */}
          <Route path="courses/add" element={<AddCourse />} />
          <Route path="courses/:id" element={<CourseDetails />} />
          <Route path="courses/:id/update" element={<UpdateCourse />} />
          {/* Notes Management — subject-centric flow */}
          <Route path="notes" element={<NotesManagementPage />} />
          <Route path="notes/add-subject" element={<AddSubjectPage />} />
          <Route path="notes/subjects/:subjectId" element={<SubjectDetailsPage />} />
          <Route path="notes/subjects/:subjectId/topics/:topicId" element={<TopicPage />} />
          <Route path="notes/subjects/:subjectId/topics/:topicId/sections/:sectionId" element={<SectionContentPage />} />
          {/* Legacy topic-level note routes (kept for backward compat) */}
          <Route path="notes/subjects/:subjectId/topics/:topicId/notes/add" element={<AddEditNote mode="add" />} />
          <Route path="notes/subjects/:subjectId/topics/:topicId/notes/:noteId" element={<NoteDetailsPage />} />
          <Route path="notes/subjects/:subjectId/topics/:topicId/notes/:noteId/edit" element={<AddEditNote mode="edit" />} />

          {/* Notes Management — course-centric flow */}
          <Route path="courses/:courseId/notes" element={<CourseNotesPage />} />
          <Route path="courses/:courseId/topics/:topicId" element={<TopicContentPage />} />
          <Route path="courses/:courseId/topics/:topicId/notes/add" element={<AddEditNote mode="add" />} />
          <Route path="courses/:courseId/topics/:topicId/notes/:noteId" element={<NoteDetailsPage />} />
          <Route path="courses/:courseId/topics/:topicId/notes/:noteId/edit" element={<AddEditNote mode="edit" />} />
          <Route path="batches" element={<BatchList />} />
          <Route path="batches/add" element={<AddBatches />} />
          <Route path="batches/:id/update" element={<UpdateBatches />} />
          <Route path="batches/:id" element={<BatchDetails />} />
          <Route path="meeting/:meetingNumber" element={<ZoomMeetings />} />
          <Route path="notes-table" element={<NotesTable />} />
          <Route path="assignments/add" element={<AddAssignments />} />
          <Route path="assignments" element={<AllAssignments />} />
          <Route path="assignments/:id" element={<AssignmentDetails />} />
          <Route path="assignments/:id/update" element={<UpdateAssignment />} />
          <Route path="questions" element={<AllQuestions />} />
          <Route path="questions/add" element={<AddQuestions />} />
          <Route path="questions/:id" element={<QuestionDetails />} />
          <Route path="questions/:id/update" element={<UpdateQuestion />} />
          <Route path="tests" element={<TestList />} />
          <Route path="tests/add" element={<AddTests />} />
          <Route path="tests/:id" element={<TestDetails />} />
          <Route path="tests/:id/update" element={<UpdateTest />} />

          {/* Student Routes */}
          <Route path="students" element={<AllStudent />} />
          <Route path="students/:id" element={<StudentDetails />} />
          <Route path="students/add" element={<AddStudent />} />
          <Route path="students/:id/update" element={<UpdateStudent />} />
          {/* Trainer Routes */}
          <Route path="trainers" element={<TrainerList />} />
          <Route path="trainers/add" element={<AddTrainer />} />
          <Route path="trainers/:id" element={<TrainerDetails />} />
          <Route path="trainers/:id/update" element={<UpdateTrainer />} />
          <Route path="notifications" element={<NotificationManagement />} />
          <Route path="leads" element={<LeadDashboard />} />
          <Route path="leads/list" element={<LeadList />} />
          <Route path="leads/setup" element={<LeadSetup />} />
          <Route path="leads/notifications" element={<LeadNotificationLog />} />
          <Route path="leads/registrations" element={<LeadRegistrations />} />
          <Route path="system-health" element={<SystemHealth />} />
          <Route path="leads/:id" element={<LeadDetails />} />
          <Route path="*" element={<PageNotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
